import React from "react";
import "./CompanyAndSharesResult.css";
import ReactPDF, { Text, View } from "@react-pdf/renderer";
import { styles } from "../styles/Slide3Styles";
import ResultsNavigationBar from "../components/ResultsNavigationBar";
import Canvas = ReactPDF.Canvas;
import { CountryType, FoundersType, TimeType } from "../ResultContext";

export function Slide3({
  foundersResult,
  vestingDurationResult,
  timeCommitmentResult,
}: {
  foundersResult: {
    name: string;
    country: CountryType;
    type: FoundersType;
  }[];
  vestingDurationResult: {
    time: number;
    type: TimeType;
  };
  timeCommitmentResult: { timeHours: string; timeWeeks: string }[];
}) {
  if (!foundersResult || !vestingDurationResult || !timeCommitmentResult) {
    return <Text>Not all information is filled in for this section</Text>;
  }
  if (foundersResult.length !== timeCommitmentResult.length) {
    return (
      <Text>Make sure to update all input fields when adding founders.</Text>
    );
  }
  return (
    <View style={styles.backgroundBlue}>
      <ResultsNavigationBar selectIdx={1} />
      <View style={styles.timelineContainer}>
        <Canvas
          style={styles.canvasBox}
          paint={(painterObject) => {
            const years = vestingDurationResult.time;

            const startX = 150;
            const startY = 100;
            const endX = 670;
            const endY = startY;
            const lengthVertical = 20;
            const interval = (endX - startX) / years;

            const accoladeStartX = startX + 5;
            const accoladeStartY = startY - lengthVertical / 2 - 10;
            const accoladeEndX = startX - 30 + years * interval - 5;
            const accoladeEndY = accoladeStartY;
            const accoladeLenght = accoladeEndX - accoladeStartX;
            const controlPointXLeft = (startX + accoladeStartX) / 2;
            const controlPointYLeft = accoladeStartY;
            const controlPointXRight = accoladeEndX + 2.5;
            const controlPointYRight = accoladeEndY;

            painterObject
              .save()
              .moveTo(startX, startY)
              .lineTo(endX, endY)
              .stroke(); //base timeline
            painterObject
              .save()
              .moveTo(endX, startY)
              .lineTo(endX, startY + 5)
              .lineTo(endX + 3, startY)
              .lineTo(endX, startY - 5)
              .fill("black"); //arrow at end of timeline

            //vertical year lines
            for (let i = 1; i < years + 1; i++) {
              painterObject
                .save()
                .moveTo(startX - 30 + i * interval, startY - lengthVertical / 2)
                .lineTo(startX - 30 + i * interval, startY + lengthVertical / 2)
                .text(
                  `${i}`,
                  startX - 30 + i * interval - 2,
                  startY + lengthVertical / 2 + 5
                )
                .stroke();
            }

            painterObject
              .save()
              .moveTo(startX, startY - lengthVertical / 2 - 5)
              .quadraticCurveTo(
                controlPointXLeft,
                controlPointYLeft,
                accoladeStartX,
                accoladeStartY
              )
              .lineTo(accoladeStartX + accoladeLenght / 2 - 4, accoladeEndY)
              .lineTo(accoladeStartX + accoladeLenght / 2, accoladeEndY - 6)
              .lineTo(accoladeStartX + accoladeLenght / 2 + 4, accoladeEndY)
              .lineTo(accoladeEndX, accoladeEndY)
              .quadraticCurveTo(
                controlPointXRight,
                controlPointYRight,
                accoladeEndX + 5,
                startY - lengthVertical / 2 - 5
              )
              .stroke(); // accolade

            painterObject
              .save()
              .text(
                "Overall Time Commitment (=Vesting Period)",
                accoladeStartX + accoladeLenght / 2 - 100,
                accoladeEndY - 20
              );

            return painterObject;
          }}
        />
      </View>
      <View
        style={
          foundersResult.length < 4
            ? styles.founderCommitmentContainerSmallMargin
            : styles.founderCommitmentContainer
        }
      >
        {foundersResult.map((item, index) => {
          return (
            <View style={styles.rolesResponsibilitiesBox} key={index}>
              <View style={styles.foundersBox}>
                <Text style={styles.textFounders}>{item.name}</Text>
              </View>
              <View style={styles.titleBox}>
                <Text style={styles.textTitleBox}>
                  Specific Time Commitment
                </Text>
              </View>
              <View style={styles.commitmentBox}>
                <Text style={styles.textCommitmentBox}>
                  {timeCommitmentResult[index].timeHours} hour(s) per week
                </Text>
                <Text style={styles.textCommitmentBox}>
                  {timeCommitmentResult[index].timeWeeks} week(s) per year
                </Text>
              </View>
            </View>
          );
        })}
      </View>
      <View
        style={
          foundersResult.length < 4
            ? styles.sideNoteContainerSmallMargin
            : styles.sideNoteContainer
        }
      >
        <View style={styles.sideNoteTextBox}>
          <Text style={styles.textSideNoteTextBox}>
            the Founders can re-evaluate on a yearly basis the above specific
            time commitments (the “Specific Time Commitment”) provided that an
            overall commitment of {vestingDurationResult.time} year(s) is
            expected from each Founder (the “Overall Time Commitment”) and in
            case of disagreement, the Specific Time Commitment shall be
            considered the default commitment of each Founder for the full
            duration of the Overall Time Commitment.
          </Text>
        </View>
      </View>
    </View>
  );
}
